import { CollageElementDataType } from '@/components/sections/builder/elements/body/collage/type';
import {
  CollectionListElementDataType,
  CollectionElementDataType,
} from '@/components/sections/builder/elements/body/collection-list/type';
import CollectionSetting from '@/components/sections/builder/settings/collection-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { cn, truncateText } from '@/lib/utils';

import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { ArrowRight, Tags } from 'lucide-react';
import React from 'react';
import { shallow } from 'zustand/shallow';
import { useRouter } from 'next/navigation';

const defaultData: CollectionElementDataType = {
  name: 'Collection',
  collection: {
    collection_name: 'Collection 1',
    collection_id: '1',
    slug: 'collection-1',
    img_url: 'https://placehold.co/600x600?text=Placeholder+Collection',
  },
};

const Content = ({
  parentData,
  data,
}: {
  parentData: CollectionListElementDataType | CollageElementDataType;
  data: CollectionElementDataType;
}) => {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const router = useRouter();

  const hasImageRatio = 'imageRatio' in parentData;
  const hasSize = 'size' in parentData;
  const hasCardStyle = 'cardStyle' in parentData;
  const hasColorSchemeCollageSection = 'colorSchemeCollageSection' in parentData;

  return (
    <>
      <div
        className={clsx(
          'image_padding',
          hasImageRatio &&
            (parentData as CollectionListElementDataType).imageRatio === 'adaptToImage' &&
            'img_ratio_adapt',
          hasImageRatio &&
            (parentData as CollectionListElementDataType).imageRatio === 'portrait' &&
            'img_ratio_portrait',
          hasImageRatio &&
            (parentData as CollectionListElementDataType).imageRatio === 'square' &&
            'img_ratio_square'
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          onClick={() => router.push(`collections/${data?.collection?.slug}`)}
          className={cn(
            'img w-full',
            hasImageRatio ? (parentData as CollectionListElementDataType).imageClass : ''
          )}
          src={data?.collection?.img_url as string}
          alt={data?.collection?.collection_name}
        />
      </div>
      <div className={clsx('py-4 pb-1 text-left')}>
        <p
          className={clsx(
            'text_content mb-2 flex items-center gap-x-2 tracking-tight',
            hasSize &&
              (parentData as CollectionListElementDataType).size === 'small' &&
              'text-base',
            hasSize && (parentData as CollectionListElementDataType).size === 'medium' && 'text-lg',
            hasSize && (parentData as CollectionListElementDataType).size === 'large' && 'text-lg'
          )}
          style={{
            color: hasColorSchemeCollageSection
              ? (parentData as CollageElementDataType).colorSchemeCollageSection?.text
              : undefined,
          }}
        >
          <a
            className={
              hasCardStyle && (parentData as CollageElementDataType).cardStyle === 'all'
                ? 'text_content card_title'
                : ''
            }
            href={`collections/${data?.collection?.slug}`}
          >
            {truncateText(
              data?.collection?.collection_name as string,
              builder.theme.collection?.limitCharacters as number
            )}
          </a>
          <ArrowRight className='h-4 w-4' />
        </p>
      </div>
      <style jsx>
        {`
          .text_content {
            color: ${hasColorSchemeCollageSection
              ? (parentData as CollageElementDataType).colorSchemeCollageSection?.text
              : 'inherit'} !important;
          }
          .img_ratio_adapt img {
            object-fit: contain !important;
          }
          .img_ratio_portrait img {
            object-fit: cover !important;
            aspect-ratio: auto 3000 / 1146 !important;
            --ratio-percent: 125% !important;
          }
          .img_ratio_square img {
            object-fit: cover !important;
            aspect-ratio: 1/1 !important;
          }
        `}
      </style>
    </>
  );
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as CollectionElementDataType;
  const parentData = (element.parentData || {}) as
    | CollectionListElementDataType
    | CollageElementDataType;

  const hasCardStyle = 'cardStyle' in parentData;
  const hasColorSchemeCollageSection = 'colorSchemeCollageSection' in parentData;
  const hasRounded = 'rounded' in parentData;

  return (
    <>
      <div
        className={clsx(
          'card-rounded  flex flex-col justify-between overflow-hidden rounded-none bg-white',
          hasCardStyle && (parentData as CollageElementDataType).cardStyle === 'all'
            ? 'theme_product_card'
            : 'theme_collection'
        )}
        data-animation='true'
        data-animation-group={`collection-item-${element.parentId}`}
      >
        <div className='bg_img bg_standard'>
          <Content parentData={parentData} data={data} />
        </div>
      </div>
      <style jsx>
        {`
          .bg-box {
            background: url(${data?.collection?.img_url});
            background-position-x: center;
            background-position-y: center;
            padding-top: 40%;
          }
          .theme_product_card {
            background-color: ${hasColorSchemeCollageSection
              ? (parentData as CollageElementDataType).colorSchemeCollageSection?.background
              : 'transparent'};
          }
          .theme_collection {
            background-color: ${hasColorSchemeCollageSection
              ? (parentData as CollageElementDataType).colorSchemeCollageSection?.background
              : 'transparent'};
          }
          .card-rounded {
            border-radius: ${hasRounded
              ? (parentData as CollectionListElementDataType).rounded || 0
              : 0}px;
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Tags;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as CollectionElementDataType;

  return (
    <div>
      <CollectionSetting
        value={data.collection}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            collection: val,
          })
        }
      />
    </div>
  );
}

const Collection = {
  type: 'collection',
  group: 'collage',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Collection;
